import React, { useEffect, useState } from "react";
import { api, formatDuration, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { LayoutGrid, Users, Activity, Radio, Megaphone, Save, Pencil, Copy, LifeBuoy, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Dashboard() {
  const [status, setStatus] = useState(null);
  const [tick, setTick] = useState(0);
  const [announcement, setAnnouncement] = useState({ text: "", radio_channel: "" });
  const [announcements, setAnnouncements] = useState([]);
  const [editingAnnId, setEditingAnnId] = useState(null);
  const [annDraft, setAnnDraft] = useState("");
  const [newAnn, setNewAnn] = useState("");
  const [addingAnn, setAddingAnn] = useState(false);
  const [editingRadio, setEditingRadio] = useState(false);
  const [draft, setDraft] = useState({ text: "", radio_channel: "" });
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  const isDirettore = user?.role === "Proprietario";

  useEffect(() => {
    const load = () => api.get("/dashboard/status").then((r) => setStatus(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.get("/announcement").then((r) => {
      setAnnouncement(r.data);
      setDraft({ text: r.data.text, radio_channel: r.data.radio_channel });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const saveAnnouncement = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/announcement", draft);
      setAnnouncement(data);
      setEditingRadio(false);
      toast.success("Aggiornato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const loadAnnouncements = () =>
    api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});

  useEffect(() => { loadAnnouncements(); }, []);

  const addAnnouncement = async () => {
    if (!newAnn.trim()) { toast.error("Scrivi il testo dell'annuncio"); return; }
    setSaving(true);
    try {
      await api.post("/announcements", { text: newAnn.trim() });
      setNewAnn("");
      setAddingAnn(false);
      toast.success("Annuncio aggiunto");
      await loadAnnouncements();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveAnnouncementItem = async () => {
    if (!annDraft.trim()) { toast.error("Scrivi il testo dell'annuncio"); return; }
    setSaving(true);
    try {
      await api.patch(`/announcements/${editingAnnId}`, { text: annDraft.trim() });
      setEditingAnnId(null);
      toast.success("Annuncio aggiornato");
      await loadAnnouncements();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deleteAnnouncement = async (ann) => {
    if (!window.confirm("Eliminare questo annuncio?")) return;
    try {
      await api.delete(`/announcements/${ann.id}`);
      toast.success("Annuncio eliminato");
      await loadAnnouncements();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Annuncio copiato negli appunti");
    } catch {
      toast.error("Copia non disponibile");
    }
  };

  const activeStaff = status?.active_staff || [];

  return (
    <div className="w-full">
      <PageHeader
        icon={LayoutGrid}
        title="Dashboard"
        subtitle="Analisi delle performance in tempo reale."
        right={
          <div className="ot-label flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-500 rounded-full ot-blink" />
            LIVE
          </div>
        }
      />

      {/* Announcement + Radio */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 mb-6 ot-fade-up">
        <div className="lg:col-span-3 ot-panel p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue-400 via-blue-300 to-blue-500" />
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-blue-400" />
              <div className="ot-label">ANNUNCI GIORNALIERI</div>
              <span className="ot-mono text-[0.6rem] text-emerald-400 tracking-widest border border-emerald-400/40 rounded px-1.5 py-0.5">{announcements.length} LIVE</span>
            </div>
            {isDirettore && !addingAnn && (
              <button
                onClick={() => setAddingAnn(true)}
                className="ot-btn-ghost !py-1 !px-2 flex items-center gap-1.5 text-xs"
                data-testid="add-ad-btn"
              >
                <Plus className="w-3 h-3" />
                Nuovo
              </button>
            )}
          </div>

          {addingAnn && (
            <div className="space-y-2 mb-3" data-testid="new-ad-box">
              <textarea
                data-testid="new-ad-textarea"
                className="ot-input w-full min-h-[90px] resize-y"
                placeholder="Testo del nuovo annuncio..."
                value={newAnn}
                onChange={(e) => setNewAnn(e.target.value)}
              />
              <div className="flex gap-2">
                <button onClick={addAnnouncement} disabled={saving} className="ot-btn-primary flex items-center gap-2" data-testid="save-new-ad-btn">
                  <Save className="w-3.5 h-3.5" />
                  {saving ? "Salvo..." : "Aggiungi Annuncio"}
                </button>
                <button onClick={() => { setAddingAnn(false); setNewAnn(""); }} className="ot-btn-ghost">Annulla</button>
              </div>
            </div>
          )}

          <div className="space-y-2" data-testid="announcements-list">
            {announcements.length === 0 && !addingAnn && (
              <div className="ot-panel p-5 text-slate-400 text-sm">Nessun annuncio attivo.</div>
            )}
            {announcements.map((a) => (
              <div key={a.id} className="ot-panel p-4" data-testid={`announcement-${a.id}`}>
                {editingAnnId === a.id ? (
                  <div className="space-y-2">
                    <textarea
                      data-testid="ad-textarea"
                      className="ot-input w-full min-h-[90px] resize-y"
                      value={annDraft}
                      onChange={(e) => setAnnDraft(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <button onClick={saveAnnouncementItem} disabled={saving} className="ot-btn-primary flex items-center gap-2" data-testid="save-ad-btn">
                        <Save className="w-3.5 h-3.5" />
                        {saving ? "Salvo..." : "Salva"}
                      </button>
                      <button onClick={() => setEditingAnnId(null)} className="ot-btn-ghost">Annulla</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row items-start gap-3">
                    <div className="flex-1 min-w-0 w-full">
                      <div className="text-base text-slate-100 break-words leading-relaxed font-medium tracking-wide" data-testid="announcement-text">
                        {a.text}
                      </div>
                      {a.updated_at && (
                        <div className="mt-2 ot-mono text-[0.65rem] text-slate-500 tracking-widest">
                          @{a.updated_by} · {new Date(a.updated_at).toLocaleString("it-IT")}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                      <button onClick={() => copyText(a.text)} className="ot-btn-ghost !py-1 !px-2 flex items-center gap-1.5 text-xs" data-testid={`copy-ad-${a.id}`}>
                        <Copy className="w-3 h-3" />
                        Copia
                      </button>
                      {isDirettore && (
                        <>
                          <button
                            onClick={() => { setEditingAnnId(a.id); setAnnDraft(a.text); }}
                            className="ot-btn-ghost !py-1 !px-2 flex items-center gap-1.5 text-xs"
                            data-testid={`edit-ad-${a.id}`}
                          >
                            <Pencil className="w-3 h-3" />
                            Modifica
                          </button>
                          <button onClick={() => deleteAnnouncement(a)} className="p-1.5 rounded text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`delete-ad-${a.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="ot-panel p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500" />
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Radio className="w-5 h-5 text-blue-400 ot-blink" />
              <div className="ot-label">RADIO OFFICINA</div>
            </div>
            {isDirettore && !editingRadio && (
              <button
                onClick={() => { setDraft({ text: announcement.text, radio_channel: announcement.radio_channel }); setEditingRadio(true); }}
                className="ot-btn-ghost !py-1 !px-2 flex items-center gap-1.5 text-xs"
                data-testid="edit-radio-btn"
              >
                <Pencil className="w-3 h-3" />
                Modifica
              </button>
            )}
          </div>
          {!editingRadio ? (
            <>
              <div className="ot-timer text-6xl font-black text-blue-400 mb-2" data-testid="radio-channel">
                {announcement.radio_channel || "360"}
              </div>
              <div className="ot-mono text-xs text-slate-500 tracking-widest">CANALE CIFRATO</div>
              <div className="mt-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-400 rounded-full ot-blink" />
                <span className="ot-mono text-[0.7rem] text-emerald-400 tracking-widest">ON AIR</span>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <label className="ot-label block">Canale radio</label>
              <input
                data-testid="radio-input"
                className="ot-input ot-mono text-2xl font-bold text-center text-blue-400"
                value={draft.radio_channel}
                onChange={(e) => setDraft({ ...draft, radio_channel: e.target.value })}
              />
              <div className="flex gap-2">
                <button onClick={saveAnnouncement} disabled={saving} className="ot-btn-primary flex-1 flex items-center justify-center gap-2" data-testid="save-radio-btn">
                  <Save className="w-3.5 h-3.5" />
                  {saving ? "Salvo..." : "Salva"}
                </button>
                <button onClick={() => setEditingRadio(false)} className="ot-btn-ghost">Annulla</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Support CTA */}
      <button
        onClick={() => nav("/supporto")}
        data-testid="dashboard-support-btn"
        className="w-full ot-panel p-5 mb-6 flex items-center justify-between hover:border-blue-400/40 transition-colors group text-left ot-fade-up"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-md border border-blue-400/40 bg-blue-400/5 flex items-center justify-center group-hover:bg-blue-400/10 transition-colors">
            <LifeBuoy className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <div className="ot-heading font-bold text-slate-100 uppercase text-lg leading-tight">
              {isDirettore ? "Gestione Ticket di Supporto" : "Serve aiuto?"}
            </div>
            <div className="ot-mono text-[0.7rem] text-slate-500 tracking-widest mt-0.5">
              {isDirettore
                ? "VISUALIZZA E RISOLVI I TICKET DEI DIPENDENTI"
                : "APRI UN TICKET PER SEGNALARE FATTURE ERRATE O CHIEDERE ASSISTENZA"}
            </div>
          </div>
        </div>
        <div className="ot-mono text-blue-400 tracking-widest text-sm group-hover:translate-x-1 transition-transform">
          APRI →
        </div>
      </button>

      {/* Status + operators */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <div className="ot-panel p-6 relative overflow-hidden lg:col-span-2 ot-fade-up">
          <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-emerald-500" />
          <div className="ot-label mb-3">STATUS SISTEMA</div>
          <div className="flex items-baseline gap-3">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 bg-emerald-500 rounded-sm ot-blink" />
              <span className="ot-heading text-3xl font-black uppercase text-slate-100">ATTIVO</span>
            </div>
            <span className="ot-mono text-slate-500 text-xs tracking-widest">/ CANALE APERTO</span>
          </div>
          <div className="mt-4 flex items-center gap-4 text-xs text-slate-500 ot-mono tracking-widest">
            <span className="flex items-center gap-1.5"><Radio className="w-3 h-3 text-blue-400" /> CRITTOGRAFIA ATTIVA</span>
            <span>·</span>
            <span>SYNC {new Date().toLocaleTimeString("it-IT")}</span>
          </div>
        </div>

        <div className="ot-panel p-6 ot-fade-up" style={{ animationDelay: "80ms" }}>
          <div className="ot-label mb-3">N° OPERATORI IN SERVIZIO</div>
          <div className="flex items-baseline gap-3">
            <div className="ot-mono text-6xl font-black text-blue-400" style={{ textShadow: "0 0 24px rgba(0,229,255,0.4)" }}>
              {status?.active_operators ?? 0}
            </div>
            <div className="text-slate-400 text-base">Dipendent{status?.active_operators === 1 ? "e" : "i"}</div>
          </div>
          <div className="mt-3 text-xs text-slate-500">
            Aggiornato in tempo reale · Totale registrati: <span className="text-slate-300">{status?.total_operators ?? 0}</span>
          </div>
        </div>
      </div>

      {/* Staff */}
      <div className="ot-fade-up" style={{ animationDelay: "160ms" }}>
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-4 h-4 text-blue-400" />
          <h2 className="ot-heading text-2xl font-bold uppercase tracking-tight">Staff Operativo</h2>
          <div className="flex-1 h-px bg-white/5" />
          <span className="ot-mono text-[0.7rem] text-slate-500 tracking-widest">{activeStaff.length} IN LINEA</span>
        </div>

        {activeStaff.length === 0 ? (
          <div className="ot-panel p-10 text-center">
            <Activity className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <div className="text-slate-400">Nessun operatore in servizio al momento.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeStaff.map((s) => {
              const started = new Date(s.start_at);
              const elapsed = Math.floor((Date.now() - started.getTime() + tick * 0) / 1000);
              const initials = (s.display_name || s.username).split(/[\s._-]+/).map((x) => x[0]).join("").slice(0, 2).toUpperCase();
              return (
                <div key={s.id} className="ot-panel p-4 flex items-center gap-3 hover:border-blue-400/30 transition-colors" data-testid={`active-staff-${s.username}`}>
                  <div className="w-11 h-11 rounded-md border border-blue-400/40 bg-blue-400/10 flex items-center justify-center ot-heading font-bold text-blue-400">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="ot-heading font-bold text-slate-100 truncate uppercase text-sm tracking-wide">{s.display_name || s.username}</div>
                    <div className="ot-mono text-[0.65rem] text-blue-400/80 tracking-widest uppercase">{s.role}</div>
                    <div className="ot-mono text-xs text-slate-400 mt-1">Sessione: <span className="text-blue-400">{formatDuration(elapsed)}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-10 text-center ot-mono text-[0.7rem] text-slate-600 tracking-widest">
        LOS SANTOS CUSTOM v1.0 · SISTEMA GESTIONE TURNAZIONI · UTENTE {user?.username?.toUpperCase()}
      </div>
    </div>
  );
}
